import { DrinkFrequency } from "@dcc/types/drinkFrequency";
import { Drinks } from "@dcc/types/drinks";

import { DrinksPerDay, DrinksWithTotal } from "@dcc/types/drinkingDay";
import { getDiseaseRisk } from "@dcc/lookup/consumptionrisk/consumptionrisk";
import { daysOfWeekText } from "@dcc/types/dayOfWeek";
import { DrinksIndexer } from "@dcc/types/drinks";
import { RiskLevel } from "@dcc/types/riskLevel";
import { DrinksPerDayStr, SaveScoreData } from "./types";
import {
  roundToNDecimalPlaces,
  roundUpToNDecimalPlaces,
  percentageAsDecimal
} from "@dcc/common/rounding";
import { drinkToNumber } from "@dcc/constants/drinkConstants";
import {
  saveResults,
  SaveDrinkCompareCalculatorResultsRequest
} from "drinkaware-tools-api-library";
import { toolsApi } from "@dcc/api";
import { SaveDrinkCompareCalculatorResultsRequestDrinkHistoryItem } from "drinkaware-tools-api-library/dist/src/clients/tools/src/models";
import { Gender } from "@dcc/types/gender";

const formatDrinks = (dayDesc: string, drinks: DrinksIndexer<number>) =>
  Object.entries(drinks).reduce(
    (agg: SaveDrinkCompareCalculatorResultsRequestDrinkHistoryItem[], curr) => {
      const [key, value] = curr;
      if (value > 0) {
        agg.push({
          quantity: value,
          day: {
            description: dayDesc
          },
          drinkId: drinkToNumber[key as Drinks]
        });
      }
      return agg;
    },
    []
  );

const formatDrinkingDays = (
  days: DrinksPerDayStr[]
): SaveDrinkCompareCalculatorResultsRequestDrinkHistoryItem[] => {
  return days.reduce(
    (agg: SaveDrinkCompareCalculatorResultsRequestDrinkHistoryItem[], curr) => {
      agg = agg.concat(
        formatDrinks(curr.day, {
          beer: curr.beer,
          wine: curr.wine,
          pint: curr.pint,
          spirit: curr.spirit
        })
      );
      return agg;
    },
    []
  );
};

const setWeekDayForDrinks = (drinkingDay: DrinksPerDay): DrinksPerDayStr => {
  return {
    ...drinkingDay,
    day: daysOfWeekText[drinkingDay.day].short
  };
};

const setMonthlyDayForDrinks = (
  drinkingDay: DrinksWithTotal
): DrinksPerDayStr => {
  return {
    ...drinkingDay,
    day: "drinking_day"
  };
};

export const buildRequestData = (
  scoreData: SaveScoreData
): SaveDrinkCompareCalculatorResultsRequest => {
  const isWeeklyDrinker = scoreData.drinkFrequency === DrinkFrequency.Weekly;

  let drinksPerDay: DrinksPerDayStr[];
  if (scoreData.drinkFrequency === DrinkFrequency.Weekly) {
    drinksPerDay = scoreData.drinksForEachWeekday.map(setWeekDayForDrinks);
  } else {
    drinksPerDay = scoreData.actualDrinksWithoutDays.map(
      setMonthlyDayForDrinks
    );
  }

  const riskOfDisease =
    getDiseaseRisk({
      riskLevel: scoreData.riskLevel,
      gender: scoreData.gender,
      weeklyUnits: scoreData.weeklyUnitsAndCalories.units,
      noDrinkingDays: drinksPerDay.length
    }) || 0;

  const drinkHistory = formatDrinkingDays(drinksPerDay);

  const percentageWhoDrinkLess = percentageAsDecimal(
    scoreData.percentWhoDrinkLess
  );

  return {
    postcode: scoreData.postcode,
    drinkFrequency: scoreData.drinkFrequency,
    bingeFrequency: scoreData.bingeFrequency,
    unitsPerTypicalDay: roundToNDecimalPlaces(
      scoreData.dailyUnitsAndCalories.units,
      2
    ),
    riskLevel: RiskLevel[scoreData.riskLevel],
    weeklyCalories: roundToNDecimalPlaces(
      scoreData.weeklyUnitsAndCalories.calories,
      2
    ),
    weeklyUnits: roundUpToNDecimalPlaces(
      scoreData.weeklyUnitsAndCalories.units,
      2
    ),
    percentDrinkLess: percentageWhoDrinkLess,
    riskOfDisease: roundToNDecimalPlaces(riskOfDisease, 0),
    amountAboveLowRisk: roundToNDecimalPlaces(scoreData.amountAboveLowRisk, 1),
    auditCScore: scoreData.auditCScore,
    drinkingDaysPerMonth: scoreData.drinkingDaysPerMonth,
    ukAverageRatio: percentageWhoDrinkLess,
    isWeeklyDrinker: isWeeklyDrinker,
    drinksPerWeek: scoreData.totalWeeklyDrinks,
    drinkHistory: drinkHistory,
    age: scoreData.age,
    sex: Gender[scoreData.gender]
  };
};

export const sendSaveScoreRequest = (
  scoreData: SaveScoreData,
  bearerToken: string | undefined
) => {
  const body = buildRequestData(scoreData);
  saveResults({
    body,
    clientId: scoreData.clientId,
    bearerToken,
    tools: toolsApi
  });
};
